import React, { useEffect, useMemo, useState } from 'react';
import { buildUiHelpers } from './sharedUi';
import { getFiniteNumber, getPositionProfitLoss, getProfitLossTotal, getProfitLossPercentTotal, getPositionAnalysis } from './depotValuation';

function DepotSectionHeader({ label, rows, color, background, onCopyTickers, borderColor, colSpan = 10 }) {
  if (rows.length === 0) return null;
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{
          padding: '10px 16px',
          fontSize: '14px',
          fontWeight: '900',
          color,
          background,
          borderBottom: `1px solid ${borderColor}`,
          textTransform: 'none',
          letterSpacing: '0.01em',
        }}
      >
        <span onClick={() => onCopyTickers(rows, label)} style={{ cursor: 'pointer' }}>
          {label} ({rows.length})
        </span>
      </td>
    </tr>
  );
}

function formatProfitLoss(value, formatMoney, currencyLabel) {
  if (!Number.isFinite(value)) return '—';
  const roundedValue = Math.round(value);
  const prefix = roundedValue > 0 ? '+' : '';
  return `${prefix}${formatMoney(roundedValue).replace(/,00$/, '')}${currencyLabel ? ` ${currencyLabel}` : ''}`;
}

function formatProfitLossPercent(value) {
  if (!Number.isFinite(value)) return '—';
  const roundedValue = Math.round(value);
  const prefix = roundedValue > 0 ? '+' : '';
  return `${prefix}${roundedValue}%`;
}

function getProfitLossColor(value, COLORS) {
  if (!Number.isFinite(value) || value === 0) return COLORS.muted;
  return value > 0 ? COLORS.green : COLORS.red;
}

function formatPlainMoney(value, formatMoney, currencyLabel) {
  const numericValue = getFiniteNumber(value);
  if (numericValue === null) return '—';
  return `${formatMoney(Math.round(numericValue)).replace(/,00$/, '')}${currencyLabel ? ` ${currencyLabel}` : ''}`;
}

function buildDepotStatsSignature(rows) {
  return rows
    .map((row) => [
      row?.id,
      row?.symbol,
      row?.index,
      row?.menge,
      row?.einstandskurs,
      row?.kurs,
      row?.summe,
      row?.rein_datum,
    ].join(':'))
    .join('|');
}

function getAnalysisExitTotal(rows) {
  const values = rows.map(row => getPositionAnalysis(row).exitPl);
  return values.length && values.every(value => value !== null)
    ? values.reduce((sum, value) => sum + value, 0) : null;
}

function getAnalysisExitPercentTotal(rows) {
  const total = getAnalysisExitTotal(rows);
  const costs = rows.map(row => getFiniteNumber(row?.summe));
  const basis = costs.reduce((sum, cost) => sum + (cost || 0), 0);
  return total !== null && costs.every(cost => cost !== null) && basis > 0 ? (total / basis) * 100 : null;
}

function ValuationNotice({ rows }) {
  const missing = rows.filter(row => getPositionProfitLoss(row) === null).length;
  return missing ? <span style={{ fontSize: '12px' }}> · Bewertung unvollständig ({missing} {missing === 1 ? 'Position' : 'Positionen'} ohne Bewertung)</span> : null;
}

const ANALYSIS_TOOLTIPS = {
  thresholdSummary: 'Zeigt, wo das Depot ungefähr stehen würde, wenn alle Positionen ihre aktuelle aktive Regelschwelle erreichen.',
  thresholdPl: 'Zeigt, wo die einzelne Position ungefähr stehen würde, wenn sie ihre aktuelle aktive Regelschwelle erreicht.',
  buffer: 'Zeigt den Teil des aktuellen Buchgewinns, der oberhalb der aktuellen aktiven Regelschwelle liegt.',
  distance: 'Zeigt, wie weit der aktuelle Kurs prozentual über oder unter der aktuellen aktiven Regelschwelle liegt.',
};

function DepotColumnsRow({ SortHeader, th, depotSort, toggleDepotSort, analysisMode }) {
  return (
    <tr style={{ background: '#ffffff05' }}>
      <SortHeader label="Rang" sortKey="rang" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '40px' }} />
      <SortHeader label="Ticker" sortKey="ticker" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '62px' }} />
      <SortHeader label="Name" sortKey="name" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '140px' }} />
      <SortHeader label="Branche" sortKey="industry" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '190px' }} />
      {!analysisMode && (
        <SortHeader label="Kurs" sortKey="kurs" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '92px' }} />
      )}
      <SortHeader label="RSL" sortKey="rsl_titel" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '58px' }} />
      {analysisMode ? (
        <>
          <SortHeader label="P/L Schwelle" sortKey="analysis_exit_pl" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '148px' }} title={ANALYSIS_TOOLTIPS.thresholdPl} />
          <SortHeader label="Puffer" sortKey="analysis_unsafe_gain" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '132px' }} title={ANALYSIS_TOOLTIPS.buffer} />
          <SortHeader label="Abstand %" sortKey="analysis_distance_pct" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '86px' }} title={ANALYSIS_TOOLTIPS.distance} />
        </>
      ) : (
        <>
          <SortHeader label="Signal" sortKey="signal" activeSort={depotSort} onToggle={toggleDepotSort} style={{ width: '74px' }} />
          <SortHeader label="Kostenbasis" sortKey="cost_basis" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '100px' }} />
          <SortHeader label="Marktwert" sortKey="market_value" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '100px' }} />
          <SortHeader label="P/L" sortKey="pl" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '78px' }} />
          <SortHeader label="P/L %" sortKey="pl_percent" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={{ width: '58px' }} />
        </>
      )}
      <th style={{ ...th, width: '38px' }} />
    </tr>
  );
}

export default function DepotSection({
  depot,
  depotLaden,
  depotSort,
  toggleDepotSort,
  depotSP500,
  depotHDAX,
  depotNASDAQ100,
  depotSTOXX600,
  depotCustom,
  entfernen,
  speichernDepotPosition,
  addDepotTranche,
  deleteDepotTranche,
  tickerInZwischenablage,
  uiSettings,
  themeMode,
  manuellerTicker,
  setManuellerTicker,
  manuellHinzufuegen,
  tickerFehler,
  tickerErfolg,
  COLORS,
  th,
  td,
  inputStyle,
  formatMoney,
  getDepotSectionIndexRsl,
  DepotRowsComponent,
  apiRequest,
}) {
  const { Card, Btn, Badge, SortHeader } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const DepotRows = DepotRowsComponent;
  const [analysisMode, setAnalysisMode] = useState(false);
  const [depotStatsByMarket, setDepotStatsByMarket] = useState({});
  const depotTh = { ...th, padding: '9px 8px' };
  const depotTd = { ...td, padding: '10px 8px' };
  const depotColumnWidths = analysisMode
    ? ['40px', '62px', '140px', '190px', '54px', '148px', '132px', '86px', '38px']
    : ['40px', '62px', '140px', '190px', '100px', '54px', '74px', '100px', '100px', '78px', '58px', '38px'];
  const fixedDepotStatsSignature = useMemo(() => JSON.stringify({
    SP500: buildDepotStatsSignature(depotSP500),
    HDAX: buildDepotStatsSignature(depotHDAX),
    NASDAQ100: buildDepotStatsSignature(depotNASDAQ100),
    STOXX600: buildDepotStatsSignature(depotSTOXX600),
  }), [depotSP500, depotHDAX, depotNASDAQ100, depotSTOXX600]);

  useEffect(() => {
    const fixedMarkets = [
      ['SP500', depotSP500],
      ['HDAX', depotHDAX],
      ['NASDAQ100', depotNASDAQ100],
      ['STOXX600', depotSTOXX600],
    ].filter(([, rows]) => rows.length > 0);
    if (!apiRequest || fixedMarkets.length === 0) {
      setDepotStatsByMarket({});
      return undefined;
    }

    let cancelled = false;
    Promise.all(
      fixedMarkets.map(([marketKey]) => (
        apiRequest(`/depot/equity-curve?market=${encodeURIComponent(marketKey)}`)
          .then((data) => [marketKey, data?.summary || null])
          .catch(() => [marketKey, null])
      )),
    ).then((entries) => {
      if (cancelled) return;
      setDepotStatsByMarket(Object.fromEntries(entries.filter(([, summary]) => summary)));
    });

    return () => {
      cancelled = true;
    };
    // The rows are intentionally tracked through fixedDepotStatsSignature to avoid refetches on unchanged re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedDepotStatsSignature, apiRequest]);

  function renderAnalysisSummary(rows, currencyLabel) {
    const exitTotal = getAnalysisExitTotal(rows);
    const exitPercent = getAnalysisExitPercentTotal(rows);
    return (
      <span title={ANALYSIS_TOOLTIPS.thresholdSummary} style={{ color: getProfitLossColor(exitTotal, COLORS), fontWeight: '800' }}>
        Schwellen-P/L: {formatProfitLoss(exitTotal, formatMoney, currencyLabel)} {exitPercent !== null ? `(${formatProfitLossPercent(exitPercent)})` : ''}
      </span>
    );
  }

  function renderDepotPerformanceStats(marketKey, currencyLabel) {
    const stats = depotStatsByMarket[marketKey];
    if (!stats) return null;
    const returnPercent = getFiniteNumber(stats.return_percent);
    const annualizedReturnPercent = getFiniteNumber(stats.annualized_return_percent);
    const periodDays = getFiniteNumber(stats.period_days);
    const drawdownFromAthPercent = getFiniteNumber(stats.drawdown_from_ath_percent);
    const peakDepotValue = getFiniteNumber(stats.peak_depot_value);
    const hasStats = returnPercent !== null || annualizedReturnPercent !== null || drawdownFromAthPercent !== null;
    if (!hasStats) return null;

    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 18px',
        marginTop: '7px',
        color: COLORS.muted,
        fontSize: '12px',
        lineHeight: 1.5,
      }}>
        {returnPercent !== null && (
          <span>
            Tatsächlich:{' '}
            <strong style={{ color: getProfitLossColor(returnPercent, COLORS) }}>
              {formatProfitLossPercent(returnPercent)}
            </strong>
            {periodDays !== null ? ` in ${Math.round(periodDays)} Tagen` : ''}
          </span>
        )}
        {annualizedReturnPercent !== null && (
          <span>
            Wäre das 1 Jahr:{' '}
            <strong style={{ color: getProfitLossColor(annualizedReturnPercent, COLORS) }}>
              {formatProfitLossPercent(annualizedReturnPercent)} p.a.
            </strong>{' '}
            <span style={{ fontStyle: 'italic' }}>(Modellrechnung)</span>
          </span>
        )}
        {drawdownFromAthPercent !== null && (
          <span>
            DD vom ATH:{' '}
            <strong style={{ color: getProfitLossColor(drawdownFromAthPercent, COLORS) }}>
              {formatProfitLossPercent(drawdownFromAthPercent)}
            </strong>
            {peakDepotValue !== null ? ` (Peak P/L ${formatPlainMoney(peakDepotValue, formatMoney, currencyLabel)})` : ''}
          </span>
        )}
      </div>
    );
  }

  function renderFixedMarketSummary(rows, marketKey, currencyLabel, rslLabel = 'RSL Index') {
    if (rows.length === 0) return null;
    return (
      <tr>
        <td colSpan={analysisMode ? 9 : 12} style={{ ...depotTd, color: COLORS.muted, fontSize: '12px', background: '#ffffff03' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: COLORS.text, fontWeight: '800' }}>
              {rslLabel}: {getDepotSectionIndexRsl(rows) !== null ? getDepotSectionIndexRsl(rows).toFixed(2) : '–'}
            </span>
            <span style={{ color: getProfitLossColor(getProfitLossTotal(rows), COLORS), fontWeight: '800' }}>
              Buch-P/L: {formatProfitLoss(getProfitLossTotal(rows), formatMoney, currencyLabel)} {getProfitLossPercentTotal(rows) !== null ? `(${formatProfitLossPercent(getProfitLossPercentTotal(rows))})` : ''}<ValuationNotice rows={rows} />
            </span>
            {analysisMode && renderAnalysisSummary(rows, currencyLabel)}
          </div>
          {renderDepotPerformanceStats(marketKey, currencyLabel)}
        </td>
      </tr>
    );
  }

  return (
    <div>
      <Card>
        {depot.length === 0 && !depotLaden && (
          <div style={{ textAlign: 'center', padding: '48px', color: COLORS.muted }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
            Noch keine Positionen im Depot
          </div>
        )}

        {depot.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden', background: '#ffffff05' }}>
              {[
                ['normal', 'Normal'],
                ['analysis', 'Analyse-Modus'],
              ].map(([mode, label]) => {
                const active = analysisMode === (mode === 'analysis');
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAnalysisMode(mode === 'analysis')}
                    style={{
                      border: 'none',
                      borderLeft: mode === 'analysis' ? `1px solid ${COLORS.border}` : 'none',
                      background: active ? COLORS.accent : 'transparent',
                      color: active ? '#fff' : COLORS.text,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {depot.length > 0 && (
          <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                {depotColumnWidths.map((width, index) => (
                  <col key={`${analysisMode ? 'analysis' : 'normal'}-${index}`} style={{ width }} />
                ))}
              </colgroup>
              <tbody>
                <DepotSectionHeader
                  label="🇺🇸 S&P 500"
                  rows={depotSP500}
                  color={COLORS.cyan}
                  background="#06b6d410"
                  onCopyTickers={tickerInZwischenablage}
                  borderColor={COLORS.border}
                  colSpan={analysisMode ? 9 : 12}
                />
                {renderFixedMarketSummary(depotSP500, 'SP500', 'USD')}
                {depotSP500.length > 0 && (
                  <DepotColumnsRow SortHeader={SortHeader} th={depotTh} depotSort={depotSort} toggleDepotSort={toggleDepotSort} analysisMode={analysisMode} />
                )}
                <DepotRows
                  positionen={depotSP500}
                  analysisMode={analysisMode}
                  onEntfernen={entfernen}
                  onSpeichernPosition={speichernDepotPosition}
                  onAddTranche={addDepotTranche}
                  onDeleteTranche={deleteDepotTranche}
                  chartsEnabled={uiSettings.tradingview_charts_enabled}
                  themeMode={themeMode}
                  COLORS={COLORS}
                  th={depotTh}
                  td={depotTd}
                  inputStyle={inputStyle}
                />

                <DepotSectionHeader
                  label="🇩🇪 HDAX"
                  rows={depotHDAX}
                  color={COLORS.yellow}
                  background="#eab30810"
                  onCopyTickers={tickerInZwischenablage}
                  borderColor={COLORS.border}
                  colSpan={analysisMode ? 9 : 12}
                />
                {renderFixedMarketSummary(depotHDAX, 'HDAX', 'EUR')}
                {depotHDAX.length > 0 && (
                  <DepotColumnsRow SortHeader={SortHeader} th={depotTh} depotSort={depotSort} toggleDepotSort={toggleDepotSort} analysisMode={analysisMode} />
                )}
                <DepotRows
                  positionen={depotHDAX}
                  analysisMode={analysisMode}
                  onEntfernen={entfernen}
                  onSpeichernPosition={speichernDepotPosition}
                  onAddTranche={addDepotTranche}
                  onDeleteTranche={deleteDepotTranche}
                  chartsEnabled={uiSettings.tradingview_charts_enabled}
                  themeMode={themeMode}
                  COLORS={COLORS}
                  th={depotTh}
                  td={depotTd}
                  inputStyle={inputStyle}
                />

                <DepotSectionHeader
                  label="🇺🇸 Nasdaq 100"
                  rows={depotNASDAQ100}
                  color={COLORS.indigo}
                  background="#6366f110"
                  onCopyTickers={tickerInZwischenablage}
                  borderColor={COLORS.border}
                  colSpan={analysisMode ? 9 : 12}
                />
                {renderFixedMarketSummary(depotNASDAQ100, 'NASDAQ100', 'USD')}
                {depotNASDAQ100.length > 0 && (
                  <DepotColumnsRow SortHeader={SortHeader} th={depotTh} depotSort={depotSort} toggleDepotSort={toggleDepotSort} analysisMode={analysisMode} />
                )}
                <DepotRows
                  positionen={depotNASDAQ100}
                  analysisMode={analysisMode}
                  onEntfernen={entfernen}
                  onSpeichernPosition={speichernDepotPosition}
                  onAddTranche={addDepotTranche}
                  onDeleteTranche={deleteDepotTranche}
                  chartsEnabled={uiSettings.tradingview_charts_enabled}
                  themeMode={themeMode}
                  COLORS={COLORS}
                  th={depotTh}
                  td={depotTd}
                  inputStyle={inputStyle}
                />

                <DepotSectionHeader
                  label="🇪🇺 STOXX 600"
                  rows={depotSTOXX600}
                  color={COLORS.green}
                  background="#22c55e10"
                  onCopyTickers={tickerInZwischenablage}
                  borderColor={COLORS.border}
                  colSpan={analysisMode ? 9 : 12}
                />
                {renderFixedMarketSummary(depotSTOXX600, 'STOXX600', 'EUR')}
                {depotSTOXX600.length > 0 && (
                  <DepotColumnsRow SortHeader={SortHeader} th={depotTh} depotSort={depotSort} toggleDepotSort={toggleDepotSort} analysisMode={analysisMode} />
                )}
                <DepotRows
                  positionen={depotSTOXX600}
                  analysisMode={analysisMode}
                  onEntfernen={entfernen}
                  onSpeichernPosition={speichernDepotPosition}
                  onAddTranche={addDepotTranche}
                  onDeleteTranche={deleteDepotTranche}
                  chartsEnabled={uiSettings.tradingview_charts_enabled}
                  themeMode={themeMode}
                  COLORS={COLORS}
                  th={depotTh}
                  td={depotTd}
                  inputStyle={inputStyle}
                />

                {Array.from(depotCustom.reduce((groups, row) => {
                  const key = row.custom_watchlist_id || 'legacy';
                  if (!groups.has(key)) groups.set(key, []);
                  groups.get(key).push(row);
                  return groups;
                }, new Map()).entries()).map(([watchlistId, rows]) => (
                  <React.Fragment key={`custom-${watchlistId}`}>
                    <DepotSectionHeader
                      label={`🧩 ${rows[0]?.custom_watchlist_name || 'Eigene Watchlist'}`}
                      rows={rows}
                      color={COLORS.yellow}
                      background="#f59e0b10"
                      onCopyTickers={tickerInZwischenablage}
                      borderColor={COLORS.border}
                      colSpan={analysisMode ? 9 : 12}
                    />
                    <tr>
                      <td colSpan={analysisMode ? 9 : 12} style={{ ...depotTd, color: COLORS.muted, fontSize: '12px', background: '#ffffff03' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ color: COLORS.text, fontWeight: '800' }}>RSL Referenz: {getDepotSectionIndexRsl(rows) !== null ? getDepotSectionIndexRsl(rows).toFixed(2) : '–'}</span>
                          <span style={{ color: getProfitLossColor(getProfitLossTotal(rows), COLORS), fontWeight: '800' }}>
                            Buch-P/L: {formatProfitLoss(getProfitLossTotal(rows), formatMoney, '')} {getProfitLossPercentTotal(rows) !== null ? `(${formatProfitLossPercent(getProfitLossPercentTotal(rows))})` : ''}<ValuationNotice rows={rows} />
                          </span>
                          {analysisMode && renderAnalysisSummary(rows, '')}
                        </div>
                      </td>
                    </tr>
                    <DepotColumnsRow SortHeader={SortHeader} th={depotTh} depotSort={depotSort} toggleDepotSort={toggleDepotSort} analysisMode={analysisMode} />
                    <DepotRows
                      positionen={rows}
                      analysisMode={analysisMode}
                      onEntfernen={entfernen}
                      onSpeichernPosition={speichernDepotPosition}
                      onAddTranche={addDepotTranche}
                      onDeleteTranche={deleteDepotTranche}
                      chartsEnabled={uiSettings.tradingview_charts_enabled}
                      themeMode={themeMode}
                      COLORS={COLORS}
                      th={depotTh}
                      td={depotTd}
                      inputStyle={inputStyle}
                    />
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {depot.length > 0 && (
          <div style={{ marginTop: '14px', fontSize: '13px', color: COLORS.muted, lineHeight: 1.7 }}>
            <div><Badge color="green">Halten</Badge> Trend der Aktie weiterhin intakt, Position halten.</div>
            <div><Badge color="yellow">Beobachten</Badge> Trend der Aktie schwächt sich ab, Position beobachten.</div>
            <div><Badge color="red">Austauschen</Badge> Trend der Aktie gebrochen, Position schließen.</div>
          </div>
        )}

      </Card>

      <Card style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '14px', color: COLORS.muted }}>
          📌 Manuell hinzufügen
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          padding: '16px',
          background: '#ffffff04',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
        }}>
          <input
            style={{ ...inputStyle, marginBottom: 0, width: '160px' }}
            autoComplete="off"
            placeholder="Ticker (z.B. AAPL)"
            value={manuellerTicker}
            onChange={e => setManuellerTicker(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && manuellHinzufuegen()}
          />
          <Btn onClick={manuellHinzufuegen} color="green" size="sm" style={{ minWidth: '134px', minHeight: '30px' }}>+ Hinzufügen</Btn>
          {tickerFehler && <span style={{ color: COLORS.red, fontSize: '13px' }}>⚠ {tickerFehler}</span>}
          {tickerErfolg && <span style={{ color: COLORS.green, fontSize: '13px' }}>{tickerErfolg}</span>}
        </div>
        <div style={{ marginTop: '10px', fontSize: '12px', color: COLORS.muted, lineHeight: 1.7 }}>
          Der Ticker wird automatisch in S&P 500, HDAX, Nasdaq 100 oder STOXX 600 gesucht. Wenn ein Titel in mehreren Indizes enthalten ist, kannst du den Ziel-Index auswählen. Nicht erkannte Titel können nicht manuell hinzugefügt werden.
        </div>
      </Card>
    </div>
  );
}
